/**
 * Plano (docs/spec/planos.md, "O que a pessoa recebe"): qual é e até quando, o espaço usado e o maior
 * arquivo, e a comparação de todos os planos, já com os tetos do servidor. Sem compra nesta fase:
 * quem muda o plano é a administração do servidor.
 */
import { useEffect } from 'react';
import { formatBytes } from '../bytes.js';
import { useLanguage, useT } from '../i18n/index.js';
import { ENTITLEMENT_KEYS, entitlementValue } from '../plans/planModel.js';
import { usePlanGrid, usePlanName } from '../plans/usePlanName.js';
import { DEFAULT_PLAN, PLAN_IDS, entitlementsFor, type PlanCeilings, type PlanId } from '../shared.js';
import { useApp } from '../store.js';
import { useMobile } from '../ui/mobile.js';

export function PlanSection() {
  const t = useT();
  const plan = useApp((s) => s.plan);
  const ceilings = useApp((s) => s.planCeilings);
  const usage = useApp((s) => s.fileUsage);
  const refreshFiles = useApp((s) => s.refreshFiles);
  const language = useLanguage((s) => s.language);
  const planName = usePlanName();
  // o espaço usado vem da lista de arquivos
  useEffect(() => {
    void refreshFiles();
  }, [refreshFiles]);
  if (!plan) return null;
  const space = plan.entitlements.storageBytes;
  const ratio = usage ? Math.min(1, usage.used / Math.max(1, space)) : 0;
  const full = !!usage && usage.used >= space;
  const day = (ms: number) => new Date(ms).toLocaleDateString(language, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const validity = plan.expiresAt !== null ? t('config.plan.until', { date: day(plan.expiresAt) }) : plan.id === DEFAULT_PLAN ? null : t('config.plan.noExpiry');
  return (
    <>
      <section className="rd-prefs__card" aria-labelledby="pref-plan-current">
        <header className="rd-prefs__cardHead">
          <h3 id="pref-plan-current">{t('config.plan.current')}</h3>
        </header>
        <div className="rd-prefs__cardBody">
          <p className="rd-plan__name">
            <strong>{planName(plan.id)}</strong>
            {validity ? <span>{validity}</span> : null}
          </p>
          {usage ? (
            <div className="rd-plan__usage" data-pref-row="storage">
              <span>{t('config.plan.used', { used: formatBytes(usage.used), total: formatBytes(space) })}</span>
              <span className="rd-plan__bar" role="meter" aria-valuemin={0} aria-valuemax={space} aria-valuenow={Math.min(usage.used, space)} aria-label={t('plans.entitlement.storageBytes')}>
                <span style={{ width: `${Math.max(ratio * 100, ratio > 0 ? 2 : 0)}%` }} className={full || ratio > 0.9 ? 'is-full' : ''} />
              </span>
              {full ? (
                <p className="rd-plan__full" role="status">
                  {t('plans.full')}
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="rd-prefs__value" data-pref-row="maxFile">
            <span>{t('plans.entitlement.maxFileBytes')}</span>
            <strong>{formatBytes(plan.entitlements.maxFileBytes)}</strong>
          </p>
        </div>
      </section>
      <section className="rd-prefs__card" aria-labelledby="pref-plan-compare" data-pref-row="compare">
        <header className="rd-prefs__cardHead">
          <h3 id="pref-plan-compare">{t('config.plan.compare')}</h3>
          <p className="rd-prefs__desc">{t('config.plan.howTo')}</p>
        </header>
        <div className="rd-prefs__cardBody">
          <PlanComparison current={plan.id} ceilings={ceilings} />
        </div>
      </section>
    </>
  );
}

/**
 * Todos os planos lado a lado, com o atual destacado. Sai de `PLANS` e `ENTITLEMENT_INFO`: um direito
 * novo na grade aparece aqui sem código novo. No celular, um cartão por plano, um embaixo do outro.
 */
function PlanComparison({ current, ceilings }: { current: PlanId; ceilings: PlanCeilings }) {
  const t = useT();
  const mobile = useMobile();
  const planName = usePlanName();
  const planGrid = usePlanGrid();
  const grid = PLAN_IDS.map((id) => ({ id, entitlements: entitlementsFor(id, ceilings, planGrid) }));
  if (mobile) {
    return (
      <ul className="rd-plans__cards">
        {grid.map(({ id, entitlements }) => (
          <li key={id} className={id === current ? 'is-current' : undefined} aria-current={id === current ? 'true' : undefined}>
            <h4>
              {planName(id)}
              {id === current ? <small>{t('config.plan.yours')}</small> : null}
            </h4>
            <dl>
              {ENTITLEMENT_KEYS.map((key) => (
                <div key={key}>
                  <dt>{t(`plans.entitlement.${key}`)}</dt>
                  <dd>{entitlementValue(t, key, entitlements[key])}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <table className="rd-plans">
      <thead>
        <tr>
          <th scope="col">
            <span className="rd-plans__corner">{t('config.plan.entitlement')}</span>
          </th>
          {grid.map(({ id }) => (
            <th key={id} scope="col" className={id === current ? 'is-current' : undefined} aria-current={id === current ? 'true' : undefined}>
              {planName(id)}
              {id === current ? <small>{t('config.plan.yours')}</small> : null}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {ENTITLEMENT_KEYS.map((key) => (
          <tr key={key}>
            <th scope="row">{t(`plans.entitlement.${key}`)}</th>
            {grid.map(({ id, entitlements }) => (
              <td key={id} className={id === current ? 'is-current' : undefined}>
                {entitlementValue(t, key, entitlements[key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
